import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { UsersIcon } from "@lucide/icons";

import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardDescription } from "simplydo/react/ui/card";
import { H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import InviteeChip from "./InviteeChip";
import type { InviteCardProps } from "./types";

const IdeaInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, inviteeUser, invitee, idea, createdByCurrentUser } =
    invitation;
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const accept = useCallback(() => {
    util
      .confirm(
        `Join ${t("generic.idea")} team`,
        `Are you sure you want to collaborate with ${inviterUser?.profile?.fullName} on the ${t("generic.idea")} "${idea?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success(`Joined ${t("generic.idea")} team`);
          },
          () => toast.error(`Error joining ${t("generic.idea")} team`),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject join",
        `Are you sure you want to reject ${inviterUser?.profile?.fullName} invitation to join the ${t("generic.idea")} "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove invitation",
        `Are you sure you want to remove your invitation to let ${inviteeUser?.profile?.fullName || invitee} join your ${t("generic.idea")} "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [
    removeInvite,
    invitation,
    idea?.name,
    inviteeUser?.profile?.fullName,
    invitee,
    t,
  ]);

  const canAccept = invitation.canAccept && !!idea;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          <H3>
            <Link to={idea ? `/ideas/${idea._id}` : ""}>
              {idea?.name || `Deleted ${t("generic.idea")}`}
            </Link>
          </H3>
        ) : (
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={UsersIcon} />
            Join {idea?.name || `Deleted ${t("generic.idea")}`}
          </H2>
        )}
        <CardDescription>
          {createdByCurrentUser ? (
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText={`to join this ${t("generic.idea")}`}
            />
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText={`has invited you to join this ${t("generic.idea")}`}
            />
          )}
        </CardDescription>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? { display: "flex", justifyContent: "flex-end" }
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && idea && (
            <ButtonLink
              color="green"
              variant="outline"
              to={`/ideas/${idea?._id}`}
            >
              Go to {t("generic.idea")}
            </ButtonLink>
          )}

          {!invitation.acceptedAt && (
            <>
              {!idea && (
                <Message
                  style={{ width: "100%" }}
                  color="red"
                >{`This invitation to join is no longer valid because the ${t("generic.idea")} has been deleted.`}</Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                  variant="outline"
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && canAccept ? (
                <Button color="green" onClick={accept} variant="outline">
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default IdeaInviteCard;
